import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Zap,
  Search,
  ChevronRight,
  ArrowLeft,
  ShieldPlus,
  AlertCircle,
  UserCheck,
  Plus,
  Minus,
} from "lucide-react";
import Footer from "../../components/Footer";

// --- 1. FAILURE CARDS ---
const dataSecurityFailureCards = [
  {
    title: "Data exposure is invisible",
    points: [
      "Sensitive data is not fully discovered",
      "Data locations are unclear",
      "Access remains uncontrolled",
    ],
    icon: <Search className="text-[#34d399]" size={32} strokeWidth={1.5} />,
  },
  {
    title: "Access is overly permissive",
    points: [
      "Too many users have data access",
      "SaaS and automations increase exposure",
      "Least-privilege is not enforced",
    ],
    icon: <ShieldPlus className="text-[#34d399]" size={32} strokeWidth={1.5} />,
  },
  {
    title: "DLP is noisy and broken",
    points: [
      "Business workflows are blocked",
      "Data leaks go undetected",
      "Alerts are ignored",
    ],
    icon: (
      <AlertCircle className="text-[#34d399]" size={32} strokeWidth={1.5} />
    ),
  },
  {
    title: "Insider risks go unmonitored",
    points: [
      "Sensitive activity goes unseen",
      "Pre-exit data theft is missed",
      "Threats are discovered too late",
    ],
    icon: <UserCheck className="text-[#34d399]" size={32} strokeWidth={1.5} />,
  },
];

// --- 2. REALITY CARDS ---
// const realityCards = [
//   {
//     title: "Visibility Gap",
//     heading: "Sensitive data exists everywhere, with limited visibility",
//     desc: "Most teams lack a current, accurate understanding of where sensitive data actually resides or how it’s being accessed.",
//   },
//   {
//     title: "Static Policies",
//     heading: "Static policies don’t match real usage",
//     desc: "Traditional data protection rules can’t keep up with modern collaboration, sharing, and cloud-based workflows.",
//   },
//   {
//     title: "Insider Risk",
//     heading: "Insider and accidental risk dominate",
//     desc: "Most data incidents stem from over-permissioning, mis-sharing, or compromised identities, not advanced malware.",
//   },
//   {
//     title: "Compliance Burden",
//     heading: "Compliance requires continuous proof",
//     desc: "Regulations demand evidence of ongoing data protection, not one-time assessments or screenshots.",
//   },
// ];

// --- 3. OPERATIONAL PROCESS DATA (Restored Full Data) ---
const steps = [
  {
    title: "Continuous Data Visibility & Oversight",
    desc: "We continuously monitor where sensitive data exists across SaaS, cloud platforms, databases, and file stores using customer approved tooling. Our team validates classifications and tracks data movement.",
    owns: [
      "Continuous data visibility reviews",
      "Validation of sensitive and regulated data locations",
      "Ongoing drift detection as data moves or duplicates",
    ],
  },
  {
    title: "Ongoing Data Access Risk Management",
    desc: "We regularly assess who has access to sensitive data and whether that access is still justified. We correlate data sensitivity with identity usage patterns to reduce overexposure.",
    owns: [
      "Identification of excessive and risky access",
      "Monitoring public links, external sharing, and stale users",
      "Actionable recommendations to reduce exposure",
    ],
  },
  {
    title: "Managed DLP Operations",
    desc: "We operate and continuously tune DLP controls to protect sensitive data without disrupting users. We focus on reducing false positives and ensuring policies reflect real usage.",
    owns: [
      "DLP policy tuning and optimization",
      "Alert review and false positive reduction",
      "Context-based enforcement across web, SaaS, and email",
    ],
  },
  {
    title: "Behavioral Monitoring for Data Misuse",
    desc: "We monitor abnormal access patterns that traditional controls miss, such as unusual downloads or mass sharing. We focus on identifying early signs of insider risk.",
    owns: [
      "Review of abnormal data access behavior",
      "Investigation of risky patterns",
      "Clear escalation when human risk is detected",
    ],
  },
  {
    title: "AI & Generative Data Risk Governance",
    desc: "We help monitor and control how sensitive data is shared with AI assistants and Copilots. Our role is to ensure AI usage does not quietly become a data leak.",
    owns: [
      "Oversight of sensitive data shared with AI tools",
      "Policy enforcement guidance for AI usage",
      "Visibility into prompt-based data exposure",
    ],
  },
  {
    title: "Data-Centric Incident Response",
    desc: "When data risk is identified, we act quickly to contain exposure. We focus on limiting blast radius and delivering clear evidence for audit and leadership teams.",
    owns: [
      "Guided containment actions and recommendations",
      "Data-focused incident analysis",
      "Clear, defensible reporting for stakeholders",
    ],
  },
];

// --- 4. KEY BENEFITS (Restored Full Data) ---
const benefits = [
  {
    title: "Full Visibility Into Sensitive Data",
    text: "Know what sensitive data exists, where it lives, how it moves, and who can access it across SaaS, cloud, and AI systems.",
  },
  {
    title: "Reduced Data Exposure",
    text: "Continuously identify and eliminate excessive access, public sharing, stale permissions, and risky integrations.",
  },
  {
    title: "Better Protection",
    text: "Replace noisy, keyword-based DLP with identity aware controls that protect data without blocking the business.",
  },
  {
    title: "Insider Risk Detection",
    text: "Detect gradual exfiltration, abnormal downloads, and mass sharing early before damage is done.",
  },
  {
    title: "Board-Ready Risk Outcomes",
    text: "Move from dashboards to decisions with clear answers on data risk, exposure reduction, and incident impact.",
  },
];

// --- 5. FAQ (Restored Full Data) ---
const faqs = [
  {
    q: "What is Synthera Security?",
    a: "Synthera Security provides a unified approach to securing and governing data across modern, distributed environments. By combining data security, governance, and compliance into managed services, Synthera helps organizations reduce risk and complexity while improving visibility, control, and regulatory confidence in the age of cloud and AI.",
  },
  {
    q: "What data does Synthera Security protect?",
    a: "Synthera Security helps organizations protect sensitive data across on-premises systems, multicloud platforms, SaaS applications, and AI-enabled services. This includes both structured and unstructured data, ensuring visibility and protection wherever data lives or moves.",
  },
  {
    q: "How do I get started with Synthera Security?",
    a: "Getting started with Synthera Security begins with understanding your data landscape and risk exposure. Our team works with you to assess current visibility, access, and controls, then operationalizes data security through continuous monitoring, governance, and response aligned to your business needs.",
  },
];

export default function ManagedDataSecurity() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <>
      <Helmet>
        <title>Managed Data Security Services | Synthera Security</title>

        <meta
          name="description"
          content="Managed Data Security services providing continuous data visibility, access risk management, DLP operations, insider risk detection, AI data governance, and data-centric incident response."
        />

        <meta
          name="keywords"
          content="Managed Data Security, Data Loss Prevention, DLP, Data Governance, Insider Risk, Data Protection, AI Data Security, Data Security Operations, Synthera Security"
        />

        <meta name="robots" content="index, follow" />

        <meta
          property="og:title"
          content="Managed Data Security Services | Synthera Security"
        />

        <meta
          property="og:description"
          content="Reduce data exposure, improve visibility, and strengthen protection with managed data security services."
        />

        <meta property="og:type" content="website" />

        <meta
          property="og:url"
          content="https://www.syntherasecurity.com/managed-data-security"
        />

        <link
          rel="canonical"
          href="https://www.syntherasecurity.com/managed-data-security"
        />
      </Helmet>

      <div className="bg-white min-h-screen font-sans text-slate-900">
        {/* --- HERO SECTION --- */}
        <section className="bg-[#032d28] text-white relative overflow-hidden flex flex-col items-center py-24 px-6">
          <Link
            to="/services/managed-security-services"
            className="absolute top-20 left-8 text-white/40 hover:text-[#24ff81] transition-colors"
          >
            <ArrowLeft size={24} />
          </Link>

          <div className="text-center max-w-5xl mb-16">
            <span className="text-white/40 text-xs tracking-[0.2em] uppercase font-bold mb-4 block">
              Most DLP deployments fall short
            </span>
            <h1 className="text-4xl md:text-6xl font-bold leading-tight tracking-tight">
              Traditional data security fails -{" "}
              <span className="text-[#24ff81]">risks stay unmanaged</span>
            </h1>
          </div>

          {/* --- FAILURE CARDS (TOP) --- */}
          <div className="container mx-auto max-w-7xl relative z-10 mb-32">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {dataSecurityFailureCards.map((item, index) => (
                <motion.div
                  key={index}
                  whileHover={{ y: -8 }}
                  className="group bg-white p-6 rounded-3xl shadow-lg flex flex-col min-h-[260px] cursor-pointer transition-all hover:shadow-2xl"
                >
                  <div className="mb-4">{item.icon}</div>
                  <h3 className="text-[#34d399] text-base font-bold uppercase tracking-wide mb-4 group-hover:text-[#0c3f38] transition">
                    {item.title}
                  </h3>
                  <div className="flex flex-col w-full">
                    {item.points.map((line, i) => (
                      <div key={i}>
                        <p className="text-[#0c3f38]/70 text-sm font-medium py-1 group-hover:text-[#0c3f38] transition">
                          {line}
                        </p>
                        {i !== item.points.length - 1 && (
                          <div className="h-px w-full bg-[#24ff81]/40 my-[2px]" />
                        )}
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* --- REALITY CARDS SECTION --- */}
          {/* <div className="container mx-auto max-w-7xl relative z-10">
          <div className="text-center mb-12">
            <p className="text-[#24ff81] text-sm font-bold uppercase tracking-[0.3em] opacity-80">
              The Reality Organizations Face Today
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-white/10 rounded-[45px] overflow-hidden shadow-2xl">
            {realityCards.map((card, index) => (
              <motion.div
                key={index}
                whileHover={{ backgroundColor: "#f8fafc", y: -5 }}
                className="bg-white p-10 flex flex-col text-left min-h-[380px] border-r border-gray-100 last:border-r-0 cursor-pointer transition-colors"
              >
                <h3 className="text-[#032d28] text-xl font-extrabold mb-6 leading-tight uppercase">
                  {card.heading}
                </h3>
                <p className="text-slate-600 text-base font-medium leading-relaxed">
                  {card.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div> */}
        </section>

        {/* ===== PREMIUM OPERATIONAL CARDS (Exact match to Forest style) ===== */}
        <section className="relative py-28 bg-[#071c18] overflow-hidden">
          {/* The specific radial gradient from your SASE model */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(54,168,138,.15),transparent)]" />

          <div className="container mx-auto px-6 max-w-7xl relative z-10">
            {/* Heading */}
            <div className="text-center mb-20">
              <p className="text-emerald-400 uppercase tracking-[0.3em] text-xs font-bold mb-6">
                Managed Data Security Operations
              </p>
              <h2 className="text-white text-4xl md:text-6xl font-black leading-tight">
                Operational{" "}
                <span className="text-emerald-400">Execution Model</span>
              </h2>
            </div>

            {/* Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
              {steps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="forest-card rounded-3xl p-8 group cursor-pointer"
                >
                  {/* Icon Container - Match SASE Icon Style */}
                  <div className="icon-container w-14 h-14 rounded-2xl flex items-center justify-center mb-8">
                    <ShieldPlus className="text-emerald-400" size={26} />
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-semibold text-white mb-4 group-hover:text-emerald-300 transition">
                    {step.title}
                  </h3>

                  {/* Desc */}
                  <p className="text-emerald-100/60 text-sm leading-relaxed mb-8">
                    {step.desc}
                  </p>

                  {/* Divider & Ownership Section */}
                  <div className="border-t border-emerald-800/30 pt-6">
                    <p className="text-xs font-semibold text-emerald-500/70 uppercase tracking-widest mb-5">
                      Service Ownership
                    </p>

                    <ul className="space-y-3">
                      {step.owns.map((item, i) => (
                        <li
                          key={i}
                          className="service-item flex items-center text-sm text-emerald-100/80"
                        >
                          <ChevronRight
                            className="mr-3 text-emerald-400"
                            size={16}
                          />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= KEY BENEFITS PREMIUM BALANCED ================= */}
        <section className="py-28 bg-[#071c18] relative overflow-hidden">
          {/* subtle grid */}
          <div
            className="absolute inset-0 opacity-10 
    bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),
    linear-gradient(to_bottom,#80808012_1px,transparent_1px)] 
    bg-[size:40px_40px]"
          />

          {/* heading */}
          <div className="max-w-7xl mx-auto text-center mb-20 px-6 relative z-10">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white tracking-tight">
              Key Benefits
            </h2>

            <p className="text-emerald-100/60 text-lg max-w-2xl mx-auto">
              Outcomes that strengthen security, reduce exposure, and give
              leadership confidence.
            </p>
          </div>

          <div className="max-w-6xl mx-auto relative z-10">
            {/* ROW 1 — first 3 cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {benefits.slice(0, 3).map((b, i) => (
                <div
                  key={i}
                  className="benefit-card group relative h-[230px] p-8
          bg-[#031f1c] border border-white/10
          transition-all duration-500
          hover:bg-gradient-to-br hover:from-[#0b3d35] hover:to-[#032d28]
          hover:border-emerald-400/40
          hover:shadow-[0_0_35px_rgba(36,255,129,0.12)]"
                >
                  {/* glow */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-500 pointer-events-none">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(36,255,129,0.18),transparent_60%)]" />
                  </div>

                  <div className="relative z-10">
                    <Zap className="text-emerald-400 mb-5" size={26} />
                    <h3 className="text-xl font-semibold text-white mb-3">
                      {b.title}
                    </h3>
                    <p className="text-white/70 text-sm leading-relaxed">
                      {b.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* ROW 2 — centered 2 cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 mt-0 md:max-w-3xl mx-auto">
              {benefits.slice(3, 5).map((b, i) => (
                <div
                  key={i}
                  className="benefit-card group relative h-[230px] p-8
          bg-[#031f1c] border border-white/10
          transition-all duration-500
          hover:bg-gradient-to-br hover:from-[#0b3d35] hover:to-[#032d28]
          hover:border-emerald-400/40
          hover:shadow-[0_0_35px_rgba(36,255,129,0.12)]"
                >
                  {/* glow */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-500 pointer-events-none">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(36,255,129,0.18),transparent_60%)]" />
                  </div>

                  <div className="relative z-10">
                    <Zap className="text-emerald-400 mb-5" size={26} />
                    <h3 className="text-xl font-semibold text-white mb-3">
                      {b.title}
                    </h3>
                    <p className="text-white/70 text-sm leading-relaxed">
                      {b.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* --- FAQ SECTION (RESTORED DATA) --- */}
        <section className="bg-[#071c18] py-24 border-t border-white/5">
          <div className="container mx-auto px-6 max-w-4xl">
            <h2 className="text-[#065c41] text-4xl md:text-6xl font-semibold text-center mb-16">
              FAQ
            </h2>
            <div className="space-y-0 border-t border-white/10">
              {faqs.map((faq, i) => (
                <div key={i} className="border-b border-white/10">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full py-8 flex items-center justify-between text-left group"
                  >
                    <span className="text-white text-lg md:text-xl font-medium leading-snug group-hover:text-[#24ff81] transition-colors ">
                      {faq.q}
                    </span>
                    <div className="ml-4 flex-shrink-0">
                      {openFaq === i ? (
                        <Minus size={28} className="text-[#24ff81]" />
                      ) : (
                        <Plus size={28} className="text-[#24ff81]" />
                      )}
                    </div>
                  </button>
                  <AnimatePresence>
                    {openFaq === i && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="pb-10 text-white/60 text-lg leading-relaxed font-medium">
                          {faq.a}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
